import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ItemCreate, ItemUpdate, TimelineItemWithCommentCount } from "@travel/shared";
import { ItemCreateSchema, ItemUpdateSchema } from "@travel/shared";
import { api, ApiError } from "../../api/client";
import { BottomSheet } from "../common/BottomSheet";
import { Button } from "../common/Button";
import { localInputToUtc, utcToLocalInput } from "../../utils/time";

type Target = TimelineItemWithCommentCount | "new" | null;

interface Props {
  target: Target;
  planId: string;
  onClose: () => void;
}

export function ItemEditDialog({ target, planId, onClose }: Props) {
  const isNew = target === "new";
  const item = isNew || target === null ? null : target;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [link, setLink] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (target === null) return;
    if (isNew) {
      const now = new Date();
      const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
      setTitle("");
      setDescription("");
      setStart(utcToLocalInput(now.toISOString()));
      setEnd(utcToLocalInput(inOneHour.toISOString()));
      setLink("");
    } else if (item) {
      setTitle(item.title);
      setDescription(item.description);
      setStart(utcToLocalInput(item.startUtc));
      setEnd(utcToLocalInput(item.endUtc));
      setLink(item.googleMapLink ?? "");
    }
    setError(null);
  }, [target, isNew, item]);

  const qc = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: async (input: ItemCreate | ItemUpdate) => {
      if (isNew) return api.createItem(planId, input as ItemCreate);
      if (item) return api.updateItem(item.id, input as ItemUpdate);
      throw new Error("no target");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan", planId] });
      qc.invalidateQueries({ queryKey: ["plans"] });
      onClose();
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError("저장에 실패했습니다.");
    },
  });

  function onSave() {
    setError(null);
    const candidate = {
      title,
      description,
      startDate: localInputToUtc(start),
      endDate: localInputToUtc(end),
      googleMapLink: link.trim() === "" ? undefined : link.trim(),
    };
    const schema = isNew ? ItemCreateSchema : ItemUpdateSchema;
    const result = schema.safeParse(candidate);
    if (!result.success) {
      setError(
        result.error.issues
          .map((i) => `${i.path.join(".") || "(form)"}: ${i.message}`)
          .join("\n"),
      );
      return;
    }
    saveMutation.mutate(result.data);
  }

  if (target === null) return null;

  return (
    <BottomSheet open={target !== null} onClose={onClose} title={isNew ? "일정 추가" : "일정 수정"}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave();
        }}
        className="px-4 space-y-3 pb-4"
      >
        <Field label="제목">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="form-input"
          />
        </Field>
        <Field label="설명">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="form-input font-sans"
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="시작">
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
              className="form-input"
            />
          </Field>
          <Field label="종료">
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              required
              className="form-input"
            />
          </Field>
        </div>
        <Field label="Google Map 링크 (선택)">
          <input
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://maps.google.com/..."
            className="form-input"
          />
        </Field>
        {error && (
          <pre className="bg-rose-50 text-rose-700 text-xs rounded-xl p-3 whitespace-pre-wrap">
            {error}
          </pre>
        )}
        <div className="flex gap-2 pt-1">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" className="flex-1" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "저장 중…" : "저장"}
          </Button>
        </div>
      </form>
      <style>{`
        .form-input {
          width: 100%;
          padding: 0.625rem 0.75rem;
          border: 1px solid rgb(203 213 225);
          border-radius: 0.75rem;
          font-size: 0.95rem;
          min-height: 44px;
          background: white;
        }
        .form-input:focus {
          outline: 2px solid rgb(15 23 42);
          outline-offset: -2px;
        }
      `}</style>
    </BottomSheet>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm text-slate-600 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
